import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { tokens } from "../../theme";

/**
 * Gráfico de barras apiladas (SVG, sin dependencias).
 *
 * Una barra por elemento, partida en tantos tramos como series. Se dibuja a mano
 * porque el kit no trae gráficos y traer una librería entera para tres series no
 * se paga; los colores salen de `tokens`, como todo lo demás.
 *
 * El ancho de barra es fijo y el contenedor scrollea: con cuarenta docentes, repartir
 * el ancho disponible dejaba barras de tres píxeles. Los nombres van rotados bajo su
 * barra, y el detalle exacto (cada serie y el total) va en el tooltip.
 */

export interface SerieBarra {
  key: string;
  label: string;
  color: string; // de `tokens`; nunca un hex literal
}

export interface DatoBarra {
  key: string | number;
  label: string; // el nombre bajo la barra y en el tooltip
  valores: Record<string, number>;
}

const ANCHO_BARRA = 22;
const SEPARACION = 12;
const MARGEN = { arriba: 12, derecha: 8, abajo: 96, izquierda: 34 };

const TRAMOS = 4; // divisiones del eje Y a las que se aspira

/**
 * Escala del eje Y: un paso "redondo" que divide el techo en partes iguales.
 *
 * Se elige el paso y **después** el techo, para que la última marca caiga justo
 * arriba: calcularlos al revés dejaba etiquetas pegadas (un techo de 10 con paso 3
 * marcaba 0-3-6-9 y encima el 10). Los pasos son enteros porque el eje cuenta
 * estudiantes.
 */
function escala(max: number): { techo: number; paso: number } {
  const objetivo = Math.max(max, 1) / TRAMOS;
  const magnitud = 10 ** Math.floor(Math.log10(objetivo));
  const paso = [1, 2, 3, 4, 5, 10].map((p) => p * magnitud).find((p) => p >= objetivo) ?? 10 * magnitud;
  const entero = Math.max(1, Math.round(paso));
  return { techo: entero * Math.ceil(Math.max(max, 1) / entero), paso: entero };
}

/** Marcas del eje, de 0 al techo. */
function marcas(techo: number, paso: number): number[] {
  const salida: number[] = [];
  for (let v = 0; v <= techo; v += paso) salida.push(v);
  return salida;
}

export default function BarrasApiladas({
  series,
  datos,
  alto = 260,
  vacio = "No hay datos que mostrar.",
}: {
  series: SerieBarra[];
  datos: DatoBarra[];
  alto?: number;
  vacio?: string;
}) {
  if (!datos.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        {vacio}
      </Typography>
    );
  }

  const totalDe = (d: DatoBarra) => series.reduce((acc, s) => acc + (d.valores[s.key] ?? 0), 0);
  const { techo: max, paso } = escala(Math.max(...datos.map(totalDe), 1));
  const altoTrama = alto - MARGEN.arriba - MARGEN.abajo;
  const ancho = MARGEN.izquierda + datos.length * (ANCHO_BARRA + SEPARACION) + MARGEN.derecha;
  const y = (valor: number) => MARGEN.arriba + altoTrama * (1 - valor / max);

  return (
    <Box>
      {/* Leyenda arriba: sin ella, tres tonos apilados no dicen qué es cada uno. */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {series.map((s) => (
          <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 11, height: 11, borderRadius: "3px", bgcolor: s.color }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {s.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box sx={{ overflowX: "auto", pb: 0.5 }}>
        <svg width={ancho} height={alto} role="img" style={{ display: "block" }}>
          {/* Grilla + eje Y */}
          {marcas(max, paso).map((v) => (
            <g key={v}>
              <line x1={MARGEN.izquierda} x2={ancho - MARGEN.derecha} y1={y(v)} y2={y(v)} stroke={tokens.tabla.filaBorde} strokeWidth={1} />
              <text x={MARGEN.izquierda - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill={tokens.neutro.textoSuave} fontFamily={tokens.fuente.cuerpo}>
                {v}
              </text>
            </g>
          ))}

          {datos.map((d, i) => {
            const x = MARGEN.izquierda + SEPARACION / 2 + i * (ANCHO_BARRA + SEPARACION);
            const total = totalDe(d);
            let acumulado = 0;
            return (
              <Tooltip
                key={d.key}
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                      {d.label}
                    </Typography>
                    {series.map((s) => (
                      <Typography key={s.key} variant="caption" sx={{ display: "block" }}>
                        {s.label}: {d.valores[s.key] ?? 0}
                      </Typography>
                    ))}
                    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                      Total: {total}
                    </Typography>
                  </Box>
                }
                followCursor
              >
                <g>
                  {/* Zona sensible del ancho completo de la columna: apuntar a una barra
                      de 22 px —o a una de altura cero— es incómodo. */}
                  <rect x={x - SEPARACION / 2} y={MARGEN.arriba} width={ANCHO_BARRA + SEPARACION} height={altoTrama} fill="transparent" />
                  {series.map((s) => {
                    const valor = d.valores[s.key] ?? 0;
                    if (!valor) return null;
                    const altoTramo = (altoTrama * valor) / max;
                    acumulado += valor;
                    return (
                      // Un filo del color de la tarjeta entre tramo y tramo: dos series
                      // de la misma familia pegadas se leían como un solo bloque.
                      <rect
                        key={s.key}
                        x={x}
                        y={y(acumulado)}
                        width={ANCHO_BARRA}
                        height={altoTramo}
                        fill={s.color}
                        stroke={tokens.neutro.surface}
                        strokeWidth={1}
                        shapeRendering="crispEdges"
                      />
                    );
                  })}
                  <text
                    x={x + ANCHO_BARRA / 2}
                    y={MARGEN.arriba + altoTrama + 10}
                    transform={`rotate(-55 ${x + ANCHO_BARRA / 2} ${MARGEN.arriba + altoTrama + 10})`}
                    textAnchor="end"
                    fontSize={11}
                    fill={tokens.neutro.textoSuave}
                    fontFamily={tokens.fuente.cuerpo}
                  >
                    {d.label}
                  </text>
                </g>
              </Tooltip>
            );
          })}

          {/* Línea base, sobre la grilla */}
          <line
            x1={MARGEN.izquierda}
            x2={ancho - MARGEN.derecha}
            y1={y(0)}
            y2={y(0)}
            stroke={tokens.neutro.borde}
            strokeWidth={1.5}
          />
        </svg>
      </Box>
    </Box>
  );
}
